import { Base64 } from 'js-base64';
import { isNil } from '@lucidlink/tscore';
import { InvalidArgumentError, InvalidFileIdError, InvalidStateError } from '#common/types';
import { Synchronized } from '#common/util';
import { createMd5Calculator } from '#crypto/node-forge';
import { parseWasmResult } from '#wasm/common';
export const Begin = () => new Uint8Array();
export class FileSystemWasm extends Synchronized {
    mod;
    calcMd5Hash = createMd5Calculator();
    getObjectHandler = () => {
        throw new InvalidStateError('GetObjectHandler is not set!');
    };
    constructor(mod, mutex) {
        super(mutex);
        this.mod = mod;
    }
    setUp(getObjectHandler) {
        this.getObjectHandler = getObjectHandler;
        this.mod['getObjectHandler'] = async (bucketName, objectKey, offset, size) => {
            const result = await this.getObjectHandler(bucketName, objectKey, offset, size);
            return { result };
        };
        this.mod['md5Handler'] = (data) => {
            const result = this.calcMd5Hash(data);
            return { result };
        };
        this.mod['base64EUrlEncodeHandler'] = (data) => {
            const result = Base64.fromUint8Array(data, true);
            return { result };
        };
        this.mod['base64EUrlDecodeHandler'] = (data) => {
            const result = Base64.toUint8Array(data);
            return { result };
        };
    }
    async getPath(linkId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GetPath(linkId));
        });
    }
    async getPathsByIdsBulk(linkIds) {
        return await this.mutex.runExclusive(async () => {
            const entries = parseWasmResult(await this.mod.GetPathsByIdsBulk(linkIds));
            return entries.map((entry) => {
                try {
                    const path = parseWasmResult(entry);
                    return { id: entry.id, type: entry.type, path };
                }
                catch (error) {
                    return { id: entry.id, errorCode: this.toFsPathErrorCode(error) };
                }
            });
        });
    }
    toFsPathErrorCode(error) {
        if (error instanceof InvalidArgumentError) {
            return 'invalid-argument';
        }
        if (error instanceof InvalidFileIdError) {
            return 'invalid-file-id';
        }
        return 'unknown';
    }
    async getExtendedEntryKey(entryPath, resolveLink = false) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GetExtendedEntryKey(entryPath, resolveLink));
        });
    }
    async getInodeId(linkId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GetInodeId(linkId));
        });
    }
    async createHardLink(target, link) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateHardLink(target, link));
        });
    }
    async resolve(entryPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.Resolve(entryPath));
        });
    }
    async readDir(dirId, maxEntries, cursor) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.ReadDir(dirId, maxEntries, cursor));
        });
    }
    async fileExists(filePath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.FileExists(filePath));
        });
    }
    async dirExists(dirPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.DirExists(dirPath));
        });
    }
    async createLink(target, link) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateLink(target, link));
        });
    }
    async readLink(linkPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.ReadLink(linkPath));
        });
    }
    async deleteLink(linkPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.DeleteLink(linkPath));
        });
    }
    async hasAccessGrant(entryPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.HasAccessGrant(entryPath));
        });
    }
    async checkReadPermissions(linkId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CheckReadPermissions(linkId));
        });
    }
    async checkReadWritePermissions(linkId, skipAuditCheck = false) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CheckReadWritePermissions(linkId, skipAuditCheck));
        });
    }
    async getAccess(linkId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GetAccess(linkId));
        });
    }
    async createDir(dirPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateDir(dirPath));
        });
    }
    async createDirById(parentId, name) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateDirById(parentId, name));
        });
    }
    async deleteDir(dirPath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.DeleteDir(dirPath));
        });
    }
    async deleteDirById(dirId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.DeleteDirById(dirId));
        });
    }
    async createFile(filePath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateFile(filePath));
        });
    }
    async deleteFile(filePath) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.DeleteFile(filePath));
        });
    }
    async createSingleObjectFile(params) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateSingleObjectFile(params.filePath, params.encryptedUrl, params.encryptedObjectId, params.iv, params.checksum, params.size.toString(), params.dataStoreKeyId));
        });
    }
    async createHttpFile(filePath, url, size, dataStoreName) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.CreateHttpFile(filePath, url, size.toString(), dataStoreName));
        });
    }
    async updateHttpFileUrl(filePath, url, size) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.UpdateHttpFileUrl(filePath, url, size.toString()));
        });
    }
    async move(sourcePath, destinationPath, overwrite) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.Move(sourcePath, destinationPath, overwrite));
        });
    }
    async validateStorageAndDataLimit() {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.ValidateStorageAndDataLimit());
        });
    }
    async setFsLimits(fsLimits) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.SetFsLimits(fsLimits.data?.toString() ?? '0', fsLimits.entries?.toString() ?? '0', fsLimits.storage?.toString() ?? '0', fsLimits.externalFilesSize?.toString() ?? '0', fsLimits.externalFilesCount?.toString() ?? '0'));
        });
    }
    async updateFilesystemStats() {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.UpdateFileSystemStats());
        });
    }
    async updateForbidSpecialCharacters(forbid) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.SetForbiddenCharacters(forbid));
        });
    }
    async setAdminCrypto(adminGroupPrivateKeyPem) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.SetAdminCrypto(adminGroupPrivateKeyPem));
        });
    }
    async getEntryInfoById(entryId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GetEntryInfoById(entryId));
        });
    }
    async getEntryTypeById(entryId) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GetEntryTypeById(entryId));
        });
    }
    async isRootId(id) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.IsRootId(id));
        });
    }
    async generateDirectLink(baseUrl, fullPath, linkVersion) {
        return await this.mutex.runExclusive(async () => {
            return parseWasmResult(await this.mod.GenerateDirectLink(baseUrl, fullPath, linkVersion));
        });
    }
    async followDirectLink(params) {
        return await this.mutex.runExclusive(async () => {
            const paramsToWasm = {
                id: params.id,
                parentId: params.parentId,
                entryName: params.entryName,
                decryptFileName: !isNil(params.decryptFileName) && params.decryptFileName === 'yes',
            };
            return parseWasmResult(await this.mod.FollowDirectLink(paramsToWasm));
        });
    }
}
